import PackageBookingPage from './PackageBookingPage';
import PhoneInput from '../components/common/PhoneInput';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from './bookingPortalService';
import { connectBookingUpdates } from './bookingPortalRealtime';
import { startAvailabilityUpdates } from './bookingAvailabilityUpdates';
import { brandColors, safeLogo, normalizeBookingPhone, calendarDays, bookingMessage, indexClassesByDate, groupClassesByTime, gymTimezoneLabel, classAvailability, waitlistConfirmations, waitlistDepartures, bookingDepartures, hasMultipleAudiences, resolveProgrammeFilter, nextAvailabilityBoundary } from './bookingPortalHelpers';
import LegacyBookingPage from './LegacyBookingPage';
import './bookingPortal.css';

function CalendarChevron({ previous = false }) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={previous ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6'} /></svg>;
}

const calendarDateFormat = new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' });
const labelDate = date => calendarDateFormat.format(date);

function PhoneSignIn({ slug, onVerified }) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retryAt, setRetryAt] = useState(0);
  const [verifyRetryAt, setVerifyRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const inFlight = useRef(false);
  const generation = useRef(0);
  const codeInput = useRef(null);
  const phoneInput = useRef(null);
  useEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => {
    const deadline = Math.max(retryAt, verifyRetryAt);
    if (Date.now() >= deadline) return undefined;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= deadline) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAt, verifyRetryAt]);
  useEffect(() => { (sentTo ? codeInput : phoneInput).current?.focus(); }, [sentTo]);
  const cooldown = Math.max(0, Math.ceil((retryAt - now) / 1000));
  const verifyCooldown = Math.max(0, Math.ceil((verifyRetryAt - now) / 1000));
  const normalized = normalizeBookingPhone(phone);

  async function send() {
    if (inFlight.current || cooldown) return;
    if (!normalized) {
      setPhoneError('Enter a valid phone number for the selected country.');
      phoneInput.current?.focus();
      return;
    }
    setPhoneError('');
    inFlight.current = true; setBusy(true); setError('');
    const current = generation.current;
    try {
      await api.startPhoneVerification(slug, normalized);
      if (current !== generation.current) return;
      setSentTo(normalized); setCode(''); setRetryAt(Date.now() + 30000); setNow(Date.now());
    } catch (requestError) {
      if (current !== generation.current) return;
      if (requestError?.response?.status === 429) {
        const receivedAt = Date.now();
        setRetryAt(receivedAt + 600000); setNow(receivedAt);
      }
      setError(bookingMessage(requestError, 'The code could not be sent. Check your number and try again.'));
    } finally { if (current === generation.current) { inFlight.current = false; setBusy(false); } }
  }
  async function verify(event) {
    event.preventDefault();
    if (inFlight.current || Date.now() < verifyRetryAt || !/^\d{6}$/.test(code)) return;
    inFlight.current = true; setBusy(true); setError('');
    const current = generation.current;
    try {
      const result = await api.verifyPhone(slug, { phone: sentTo, otp: code, remember });
      if (current === generation.current) onVerified(result.viewer);
    } catch (requestError) {
      if (current !== generation.current) return;
      if (requestError?.response?.status === 429) {
        setRetryAt(Date.now() + 600000); setVerifyRetryAt(Date.now() + 600000); setNow(Date.now());
      }
      setError(requestError?.response?.status === 401 ? 'That code is invalid or expired. Try again or request a new code.'
        : bookingMessage(requestError, 'Verification could not be completed. Please try again.'));
    } finally { if (current === generation.current) { inFlight.current = false; setBusy(false); } }
  }
  return <section className="booking-portal-signin" aria-labelledby="booking-signin-title">
    <h2 id="booking-signin-title">Sign in to book a class</h2>
    <p>Members and new visitors use a phone number to sign in.</p>
    <form onSubmit={sentTo ? verify : event => { event.preventDefault(); send(); }}>
      <div className="milton-phone-field">Phone number
        <PhoneInput aria-label="Phone number" ref={phoneInput} type="tel" autoComplete="tel" placeholder="10-digit phone number" required value={phone} disabled={busy || !!sentTo}
          onChange={e => { setPhone(e.target.value); setPhoneError(''); setError(''); }}
          aria-invalid={phoneError ? true : undefined}
          aria-describedby={phoneError ? 'booking-phone-help booking-phone-error' : 'booking-phone-help'} /></div>
      <p id="booking-phone-help" className="booking-portal-muted">Choose your country and enter your phone number. We’ll send a six-digit code by SMS.</p>
      {phoneError && <p id="booking-phone-error" role="alert" className="booking-portal-error">{phoneError}</p>}
      {sentTo && <>
        <p role="status">Code sent to {sentTo}.</p>
        <label>Verification code<input ref={codeInput} inputMode="numeric" autoComplete="one-time-code" required
          pattern="[0-9]{6}" maxLength={6} value={code} disabled={busy} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} /></label>
        <button type="button" className="booking-portal-text-button" disabled={busy} onClick={() => {
          setSentTo(''); setCode(''); setError(''); setVerifyRetryAt(0); generation.current += 1;
        }}>Use a different number</button>
      </>}
      <label className="booking-portal-check"><input type="checkbox" checked={remember} disabled={busy} onChange={e => setRemember(e.target.checked)} />
        Remember me on this device for 30 days</label>
      <p className="booking-portal-muted">Use this only on a private device. Sign out to clear remembered access.</p>
      {error && <p role="alert" className="booking-portal-error">{error}</p>}
      <button className="booking-portal-primary" type="submit" disabled={busy || (sentTo ? code.length !== 6 || verifyCooldown > 0 : cooldown > 0)}>
        {busy ? 'Please wait…' : sentTo ? verifyCooldown ? `Try again in ${verifyCooldown}s` : 'Verify and continue' : cooldown ? `Try again in ${cooldown}s` : 'Send verification code'}</button>
      {sentTo && <button type="button" disabled={busy || cooldown > 0} onClick={send}>
        {cooldown ? `Resend in ${cooldown}s` : 'Resend code'}</button>}
    </form>
  </section>;
}

function Registration({ slug, phone, onRegistered }) {
  const [reauthenticating, setReauthenticating] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { const result = await api.registerVisitor(slug, { name: name.trim(), email: email.trim() }); onRegistered(result.viewer); }
    catch (requestError) {
      if (requestError?.response?.status === 401) setReauthenticating(true);
      else setError(bookingMessage(requestError, 'Registration could not be completed. Your details are still here; please retry.'));
    }
    finally { inFlight.current = false; setBusy(false); }
  }
  if (reauthenticating) return <>
    <p role="status" className="booking-portal-notice">Your sign-in expired. Verify your phone again to continue registration. Your details are kept while this page stays open.</p>
    <PhoneSignIn slug={slug} onVerified={person => {
      if (person.phone !== phone) { setName(''); setEmail(''); }
      setReauthenticating(false); setError(''); onRegistered(person);
    }} />
  </>;
  return <section className="booking-portal-signin"><h2>Welcome to your first class</h2>
    <p>Add your details to register with this gym. Your phone has been verified.</p>
    <form onSubmit={submit}>
      <label>Your name<input autoComplete="name" required maxLength={255} value={name} disabled={busy} onChange={e => setName(e.target.value)} /></label>
      <label>Email address<input type="email" autoComplete="email" required maxLength={320} value={email} disabled={busy} onChange={e => setEmail(e.target.value)} /></label>
      <p className="booking-portal-muted">The gym will store these details in its contact list for managing your visits.</p>
      {error && <p role="alert" className="booking-portal-error">{error}</p>}
      <button className="booking-portal-primary" disabled={busy || !name.trim()}>{busy ? 'Registering…' : 'Register and view classes'}</button>
    </form>
  </section>;
}

export function BookingConfirmation({ item, timezone, waitlistCutoffMinutes = 30, busy, onClose, onConfirm, returnFocus, fallbackFocus, error }) {
  const dialog = useRef(null);
  useEffect(() => {
    const previous = returnFocus || document.activeElement;
    const element = dialog.current;
    element.showModal();
    return () => { element.close(); (previous?.isConnected ? previous : fallbackFocus)?.focus(); };
  }, [returnFocus, fallbackFocus]);
  const canceling = item.enrolled || item.waitlisted;
  const waitlist = !canceling && item.spotsLeft <= 0;
  return <dialog ref={dialog} className="booking-portal-dialog" onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}
    aria-labelledby="booking-confirm-title" aria-busy={busy}>
    <h2 id="booking-confirm-title">{canceling ? item.enrolled ? 'Cancel your booking?' : 'Leave the waitlist?' : waitlist ? 'Join the waitlist?' : 'Book this class?'}</h2>
    <h3>{item.className}</h3>
    {item.className !== item.serviceName && <p>{item.serviceName}</p>}
    <ul className="booking-portal-facts">
      <li>{new Intl.DateTimeFormat(undefined, { timeZone: timezone, dateStyle: 'full' }).format(new Date(item.start))}</li>
      <li>{new Intl.DateTimeFormat(undefined, { timeZone: timezone, hour: 'numeric', minute: '2-digit' }).formatRange(new Date(item.start), new Date(item.end))}
        {' · '}{Math.round((new Date(item.end) - new Date(item.start)) / 60000)} min</li>
      <li>{item.coachName?.trim() ? `Coach: ${item.coachName.trim()}` : 'Coach not assigned'}</li>
      <li>{item.audience === 'adults' ? 'Adults only' : item.audience === 'kids' ? 'Kids only' : 'Kids and adults'}{' · '}
        {item.spotsLeft > 0 ? `${item.spotsLeft} ${item.spotsLeft === 1 ? 'place' : 'places'} available` : 'Full'}</li>
    </ul>
    <p className="booking-portal-muted">Times in {timezone}.</p>
    <p className="booking-portal-muted">{canceling ? item.enrolled ? 'You’ll give up your place. Any reserved trial session will be returned.' : 'You’ll leave this waitlist. No trial session was reserved for joining it.' : waitlist
      ? `A place is not guaranteed. If a place opens more than ${waitlistCutoffMinutes} ${waitlistCutoffMinutes === 1 ? 'minute' : 'minutes'} before class, you may be booked automatically and sent an SMS. A trial session is reserved only when your place is confirmed.`
      : 'One available trial session is reserved when your booking is confirmed, if you are visiting on a trial.'}</p>
    {error && <p role="alert" className="booking-portal-error">{error}</p>}
    <div className="booking-portal-dialog-actions"><button disabled={busy} onClick={onClose}>Go back</button>
      <button className="booking-portal-primary" disabled={busy} onClick={onConfirm}>{busy ? 'Please wait…' : canceling ? item.waitlisted && !item.enrolled ? 'Leave waitlist' : 'Confirm cancellation' : waitlist ? 'Join waitlist' : 'Confirm booking'}</button></div>
  </dialog>;
}

function Schedule({ slug, context, viewer, onViewer, onExpired, onContext }) {
  const [classes, setClasses] = useState([]);
  const [waitlistCutoffMinutes, setWaitlistCutoffMinutes] = useState(context.policy.waitlistCutoffMinutes ?? 30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [week, setWeek] = useState(0);
  const [day, setDay] = useState('');
  const initialService = new window.URLSearchParams(window.location.search).get('service');
  const [programme, setProgramme] = useState(initialService ? `service:${initialService}` : '');
  const [audience, setAudience] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const lock = useRef(false);
  const request = useRef(0);
  const operation = useRef(null);
  const previousClasses = useRef([]);
  const trigger = useRef(null);
  const filterInput = useRef(null);
  const realtimeUpdates = useRef(null);
  const days = calendarDays(context.timezone);
  const refresh = useCallback(async (signal, foreground = false) => {
    const current = ++request.current;
    if (foreground) setLoading(true);
    try {
      const result = await api.getClasses(slug, signal);
      if (current !== request.current || signal?.aborted) return;
      const confirmed = waitlistConfirmations(previousClasses.current, result.classes);
      const departed = waitlistDepartures(previousClasses.current, result.classes);
      const changedBookings = bookingDepartures(previousClasses.current, result.classes);
      previousClasses.current = result.classes;
      const updates = [];
      if (confirmed.length) updates.push(confirmed.length === 1
        ? `Your waitlist place for ${confirmed[0].className} is confirmed. Your class is booked.`
        : 'Your waitlist places are confirmed. Check your booked classes below.');
      if (!lock.current && departed.length) updates.push(departed.length === 1
        ? `Your waitlist status for ${departed[0].className} has changed. Check the schedule or contact the gym.`
        : 'Your waitlist statuses have changed. Check the schedule or contact the gym.');
      if (!lock.current && changedBookings.length) updates.push(changedBookings.length === 1
        ? `Your booking status for ${changedBookings[0].className} has changed. Check the schedule or contact the gym.`
        : 'Your booking statuses have changed. Check the schedule or contact the gym.');
      if (updates.length) setNotice(updates.join(' '));
      if (Number.isInteger(result.waitlistCutoffMinutes)) setWaitlistCutoffMinutes(result.waitlistCutoffMinutes);
      setClasses(result.classes); onViewer(result.viewer);
      if (result.context) onContext(result.context);
      setError('');
      if (!hasMultipleAudiences(result.classes)) setAudience('');
      setProgramme(current => resolveProgrammeFilter(result.classes, current));
    } catch (requestError) {
      if (current !== request.current || signal?.aborted) return;
      if (requestError?.response?.status === 401) onExpired();
      else {
        setNotice(''); setError(bookingMessage(requestError, 'The schedule could not be refreshed. Please retry before booking.'));
        if (requestError?.response?.status === 403) {
          try {
            const latest = await api.getPortalContext(slug);
            if (current === request.current && !signal?.aborted) {
              onContext(latest);
              if (latest.policy.enabled) {
                const person = await api.getViewer(slug);
                if (current === request.current && !signal?.aborted) onViewer(person.viewer);
              }
            }
          }
          catch { /* Keep the availability error when context is also unavailable. */ }
        }
      }
    } finally { if (current === request.current && !signal?.aborted) setLoading(false); }
  }, [slug, onViewer, onExpired, onContext]);
  useEffect(() => {
    const controller = new window.AbortController();
    const updates = startAvailabilityUpdates({
      refresh: foreground => refresh(controller.signal, foreground),
      connect: context.realtimeEnabled !== false && typeof window.EventSource === 'function'
        ? handlers => connectBookingUpdates(slug, handlers) : null,
      isVisible: () => !document.hidden, isBusy: () => lock.current, onExpired,
    });
    realtimeUpdates.current = updates;
    const update = () => updates.resume();
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => { realtimeUpdates.current = null; updates.stop(); controller.abort(); request.current += 1;
      window.removeEventListener('focus', update); document.removeEventListener('visibilitychange', update); };

  }, [refresh, slug, onExpired, context.realtimeEnabled]);
  useEffect(() => {
    const delay = nextAvailabilityBoundary(classes, context.timezone, waitlistCutoffMinutes) - Date.now();
    const timer = setTimeout(() => realtimeUpdates.current?.refreshAtBoundary(), Math.max(0, delay) + 50);
    return () => clearTimeout(timer);
  }, [classes, context.timezone, waitlistCutoffMinutes]);
  const programmes = useMemo(() => [...new Set(classes.map(c => c.className))].sort(), [classes]);
  const activeProgramme = resolveProgrammeFilter(classes, programme);
  const showAudienceFilter = hasMultipleAudiences(classes);
  const activeAudience = showAudienceFilter ? audience : '';
  const filtered = useMemo(() => classes.filter(c => (!activeProgramme || (activeProgramme.startsWith('service:') ? c.serviceId === activeProgramme.slice(8) : `class:${c.className}` === activeProgramme)) && (!activeAudience || c.audience === activeAudience || c.audience === 'all')), [classes, activeProgramme, activeAudience]);
  const classesByDate = useMemo(() => indexClassesByDate(filtered, context.timezone), [filtered, context.timezone]);
  const dates = days.slice(week * 7, week * 7 + 7);
  const isClosed = date => !context.businessHours.some(d => d.weekday === date.weekday && d.isOpen);
  const hasClasses = date => !isClosed(date) && classesByDate.has(date.key);
  const picked = dates.find(d => d.key === day && hasClasses(d)) || dates.find(hasClasses);
  const list = picked ? classesByDate.get(picked.key).classes : null;
  const groups = useMemo(() => groupClassesByTime(list || [], context.timezone), [list, context.timezone]);
  const timeFormat = useMemo(() => new Intl.DateTimeFormat(undefined, { timeZone: context.timezone, hour: 'numeric', minute: '2-digit' }), [context.timezone]);
  const openConfirmation = (item, element) => { setNotice(''); setDialogError(''); trigger.current = element; operation.current = { id: item.id, key: window.crypto.randomUUID() }; setSelected(item); };
  async function confirm() {
    if (lock.current || !selected) return;
    lock.current = true; setBusy(true); setNotice(''); setDialogError('');
    let shouldClose = false;
    const item = selected;
    try {
      if (item.enrolled || item.waitlisted) {
        await api.cancelClass(slug, item.id); setNotice(item.enrolled ? 'Booking canceled. Your place has been released.' : 'You have left the waitlist.');
      } else {
        const result = await api.reserveClass(slug, item.id, { operationKey: operation.current.key, waitlist: item.spotsLeft <= 0 });
        setNotice(result.status === 'waitlisted' ? 'You’re on the waitlist. We’ll send an SMS if your place is confirmed.' : 'Your class is booked.');
      }
      shouldClose = true;
    } catch (requestError) {
      if (requestError?.response?.status === 401) onExpired();
      const message = bookingMessage(requestError, 'The result could not be confirmed. Check your class status, then retry using the same confirmation.');
      setNotice(message); setDialogError(message);
      if ([403, 409].includes(requestError?.response?.status)) shouldClose = true;
    } finally {
      await refresh(); lock.current = false; setBusy(false);
      if (shouldClose) setSelected(null);
    }
  }
  return <>
    <div className="booking-portal-filters">
      <label htmlFor="booking-class-filter">Class<select id="booking-class-filter" ref={filterInput} value={activeProgramme} onChange={e => { setProgramme(e.target.value); setDay(''); }}>
        <option value="">All classes</option>{initialService && classes.some(c => c.serviceId === initialService) && <option value={`service:${initialService}`}>{classes.find(c => c.serviceId === initialService)?.serviceName} (all sessions)</option>}
        {programmes.map(name => <option key={name} value={`class:${name}`}>{name}</option>)}</select></label>
      {showAudienceFilter && <label htmlFor="booking-audience-filter">Who’s it for?<select id="booking-audience-filter" value={activeAudience} onChange={e => { setAudience(e.target.value); setDay(''); }}>
        <option value="">Everyone</option><option value="adults">Adults</option><option value="kids">Kids</option></select></label>}
    </div>
    {(activeProgramme || activeAudience) && <button type="button" className="booking-portal-clear-filters" disabled={busy}
      onClick={() => { setProgramme(''); setAudience(''); setDay(''); filterInput.current?.focus(); }}>Clear filters</button>}
    <div className="booking-portal-calendar-heading"><h2>{new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(dates[0].date)}</h2>
      <div><button aria-label="Previous seven days" disabled={!week || busy} onClick={() => { setWeek(w => w - 1); setDay(''); }}><CalendarChevron previous /></button>
        <button aria-label="Next seven days" disabled={week >= 3 || busy} onClick={() => { setWeek(w => w + 1); setDay(''); }}><CalendarChevron /></button></div></div>
    <div className="booking-portal-days" role="group" aria-label="Choose a class date">
      {dates.map(date => <button key={date.key} aria-pressed={date.key === picked?.key} disabled={!hasClasses(date) || busy}
        aria-label={`${date.key === days[0].key ? 'Today, ' : ''}${labelDate(date.date)}${isClosed(date) ? ', gym closed' : error ? ', availability unavailable' : !hasClasses(date) ? ', no classes' : classesByDate.get(date.key)?.available ? ', places available' : ', classes full'}`}
        onClick={() => setDay(date.key)}><span>{date.key === days[0].key ? 'Today' : new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', weekday: 'short' }).format(date.date)}</span>
        <strong>{date.date.getUTCDate()}</strong>{isClosed(date) ? <small>Closed</small> : <span className="booking-portal-date-indicator" aria-hidden="true">
          {!error && classesByDate.get(date.key)?.available && <span className="booking-portal-availability-dot" />}</span>}</button>)}
    </div>
    {notice && <p role="status" className="booking-portal-notice">{notice}</p>}
    {error && <div role="alert" className="booking-portal-error">{error}<button disabled={busy} onClick={() => refresh(undefined, true)}>Retry schedule</button></div>}
    {loading ? <p role="status">Loading classes…</p> : <>
      <div className="booking-portal-day-heading"><h2>{picked ? labelDate(picked.date) : error ? 'Schedule unavailable' : 'No classes this week'}</h2>
        {!!list?.length && <span>{list.length} {list.length === 1 ? 'class' : 'classes'}</span>}</div>
      {!list?.length && !error && <p className="booking-portal-empty">Try another week or change the class filters.</p>}
      {groups.map(group => <section className="booking-portal-time-group" key={group.label} aria-label={`${group.label} classes`}>
        <h3>{group.label}</h3><ul className="booking-portal-classes">{group.items.map(item => {
        const own = item.enrolled || item.waitlisted;
        const availability = classAvailability(item);
        const full = item.spotsLeft <= 0;
        const disabled = busy || !!error || (!own && (!viewer.canBook || (full && !item.canWaitlist)));
        const time = timeFormat.format(new Date(item.start));
        const timeParts = timeFormat.formatToParts(new Date(item.start));
        const clock = timeParts.filter(part => part.type !== 'dayPeriod').map(part => part.value).join('').trim();
        const period = timeParts.find(part => part.type === 'dayPeriod')?.value;
        return <li key={item.id}><button className="booking-portal-class-row" disabled={disabled} onClick={event => openConfirmation(item, event.currentTarget)}
            aria-describedby={`booking-class-meta-${item.id}`}
            aria-label={`${own ? item.enrolled ? 'Cancel booking for' : 'Leave waitlist for' : full ? item.canWaitlist ? 'Join waitlist for' : 'Full class:' : 'Book'} ${item.className}, ${time}`}>
          <span className="booking-portal-time"><strong><span>{clock}</span>{period && <span className="booking-portal-time-period">{period}</span>}</strong><small>{Math.round((new Date(item.end) - new Date(item.start)) / 60000)} min</small></span>
          <span className="booking-portal-class-name"><strong>{item.className}</strong>
            <span className="booking-portal-class-meta" id={`booking-class-meta-${item.id}`}>{item.className !== item.serviceName && <span>{item.serviceName}</span>}
              <span>{item.coachName?.trim() ? `Coach: ${item.coachName.trim()}` : 'Coach not assigned'}</span>
              {['adults', 'kids'].includes(item.audience) && <span>{item.audience === 'adults' ? 'Adults only' : 'Kids only'}</span>}
              {availability.text && <span className={`booking-portal-class-${availability.tone}`}>{availability.text}</span>}
            </span></span>
          <span className={`booking-portal-action${!own && !full ? ' booking-portal-action-primary' : ''}`} aria-hidden="true">
            {own ? item.enrolled ? 'Cancel' : 'Leave waitlist' : full ? item.canWaitlist ? 'Waitlist' : 'Full' : 'Book'}</span>
        </button></li>;
      })}</ul></section>)}
    </>}
    <p className="booking-portal-muted booking-portal-timezone">Times in {gymTimezoneLabel(context.timezone)}</p>
    {selected && <BookingConfirmation item={selected} timezone={context.timezone} waitlistCutoffMinutes={waitlistCutoffMinutes} busy={busy} onClose={() => setSelected(null)} onConfirm={confirm} returnFocus={trigger.current} fallbackFocus={filterInput.current} error={dialogError} />}
  </>;
}

function PortalPage({ slug }) {
  const [context, setContext] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const expired = useCallback(() => { setViewer(null); setError(''); setPhase('signin'); }, []);
  useEffect(() => {
    let active = true;
    setPhase('loading'); setError('');
    (async () => {
      try {
        const ctx = await api.getPortalContext(slug);
        if (!active) return;
        if (!ctx.policy.enabled) { setPhase('legacy'); return; }
        setContext(ctx);
        try { const result = await api.getViewer(slug); if (active) { setViewer(result.viewer); setPhase('ready'); } }
        catch (requestError) { if (requestError?.response?.status === 401) { if (active) setPhase('signin'); } else throw requestError; }
      } catch (requestError) {
        if (!active) return;
        // A backend without the new route retains the established booking flow.
        if (requestError?.response?.status === 404) setPhase('legacy');
        else { setError('The booking page could not be loaded. Please retry.'); setPhase('error'); }
      }
    })();
    return () => { active = false; };
  }, [slug, attempt]);
  const verified = useCallback(person => { setViewer(person); setPhase('ready'); }, []);
  const updatedContext = useCallback(latest => { setContext(latest); if (!latest.policy.enabled) setPhase('legacy'); }, []);
  if (phase === 'legacy') return <LegacyBookingPage />;
  const colors = brandColors(context?.branding.color);
  const logo = safeLogo(context?.branding.logo);
  return <main className="booking-portal" style={{ '--booking-accent': colors.accent, '--booking-on-accent': colors.onAccent }}>
    <div className="booking-portal-shell"><header className="booking-portal-header">
      <div>{logo && <img src={logo} alt="" className="booking-portal-logo" onError={event => { event.currentTarget.style.display = 'none'; }} />}
        <h1>{context?.name || 'Class booking'}</h1><p>Book a class</p></div>
      {phase === 'ready' && viewer && <button disabled={loggingOut} onClick={async () => {
        setLoggingOut(true); setError('');
        try { await api.signOut(slug); expired(); }
        catch { setError('Sign out could not be completed. Please retry to clear remembered access.'); }
        finally { setLoggingOut(false); }
      }}>{loggingOut ? 'Signing out…' : 'Sign out'}</button>}
    </header>
      {phase === 'loading' && <p role="status">Loading booking page…</p>}
      {error && <div role="alert" className="booking-portal-error">{error}{phase === 'error' && <button onClick={() => setAttempt(n => n + 1)}>Retry</button>}</div>}
      {phase === 'signin' && <PhoneSignIn slug={slug} onVerified={verified} />}
      {phase === 'ready' && viewer && (!viewer.canRegister ? <section><h2>Bookings are for gym members</h2>
        <p>Contact the gym to become a member, or sign out and use the phone linked to your membership.</p>
        {context.policy.membershipUrl && <a href={context.policy.membershipUrl} target="_blank" rel="noreferrer">View membership information</a>}</section>
        : !viewer.registered ? <Registration slug={slug} phone={viewer.phone} onRegistered={verified} />
          : <>
            {viewer.blockedReason && <p role="status" className="booking-portal-notice">{viewer.blockedReason}</p>}
            {!viewer.isMember && <aside className="booking-portal-credit" aria-label="Trial sessions">
              <strong>{viewer.creditsRemaining > 0 ? `${viewer.creditsRemaining} free ${viewer.creditsRemaining === 1 ? 'session' : 'sessions'} available` : 'Your free sessions are used or reserved'}</strong>
              <p>{viewer.creditsRemaining > 0 ? 'Each confirmed booking reserves one session.' : 'To book more sessions, contact the gym about becoming a member.'}</p>
              {!viewer.canBook && context.policy.membershipUrl && <a href={context.policy.membershipUrl} target="_blank" rel="noreferrer">View membership information</a>}
            </aside>}
            <Schedule slug={slug} context={context} viewer={viewer} onViewer={setViewer} onExpired={expired} onContext={updatedContext} />
          </>)}
    </div>
  </main>;
}

export default function PublicBookingPage() {
  const { slug } = useParams();
  return <PackageBookingPage key={slug} slug={slug} legacy={<PortalPage key={slug} slug={slug} />} />;
}
